import React, { useState, useEffect } from 'react';
import { type Alert, type Position, type Suspect } from '../api/types.js';
import { getFlagEmoji } from '../utils/Helpers.js';
import Spinner from './common/Spinner.js';
import { useTranslation } from '../i18n/useTranslation.js';

interface SuspectProfileProps {
  suspect: Suspect | null;
  pos: Position | null;
  onClose: () => void;
  onSaveNote: (id: string, note: string) => void;
  /** Viewers can read the analyst notes but not change them. */
  readOnly?: boolean;
  suspectsData: Record<string, Suspect>;
  alerts: Array<Alert>;
  onAppClick: (bundle: string) => void;
}

// SuspectProfile needs access to suspect data for social graph rendering
// Pass SUSPECTS_DATA and D001_DATA as props or import from a shared data module
function SuspectProfile({
  suspect,
  pos,
  onClose,
  onSaveNote,
  readOnly = false,
  suspectsData,
  alerts,
  onAppClick,
}: SuspectProfileProps) {
  const { t } = useTranslation();
  const [note, setNote] = useState(suspect?.notes ?? '');
  const [isNoteSaving, setNoteSaving] = useState(false);
  useEffect(() => {
    if (!suspect) return;

    setNote(suspect.notes ?? '');
    setNoteSaving(false);
  }, [suspect?.id]);

  const saveNote = async () => {
    const value = note.trim();
    if (readOnly || suspect!.notes === value) return;

    setNoteSaving(true);
    await onSaveNote(suspect!.id, value);
    setNoteSaving(false);
  };

  const score = pos?.score ?? suspect?.baseScore ?? 0;
  const nearDist = pos?.nearDist ?? 9999;

  function socialSVG(s) {
    const cx = 260,
      cy = 80,
      r = 100;
    let svg = `<svg class="svg-graph" viewBox="0 0 520 160">`;
    const conns = s.connections;
    conns.forEach((c, i) => {
      const angle = (i / Math.max(conns.length, 1)) * Math.PI * 2;
      const x2 = cx + r * Math.cos(angle),
        y2 = cy + r * Math.sin(angle);
      svg += `<line x1="${cx}" y1="${cy}" x2="${x2}" y2="${y2}" stroke="rgba(212,175,55,.25)" stroke-width="1.5" stroke-dasharray="4 3"/>`;
    });
    svg += `<circle cx="${cx}" cy="${cy}" r="30" fill="rgba(212,175,55,.15)" stroke="var(--gold)" stroke-width="1.5"/>`;
    svg += `<text x="${cx}" y="${
      cy + 4
    }" text-anchor="middle" fill="var(--gold)" font-size="9" font-family="JetBrains Mono" font-weight="700">${`...${s.id.slice(
      s.id.length - 6
    )}`}</text>`;
    conns.forEach((c, i) => {
      const angle = (i / Math.max(conns.length, 1)) * Math.PI * 2;
      const x2 = cx + r * Math.cos(angle),
        y2 = cy + r * Math.sin(angle);
      const sc = suspectsData?.[c];
      const col = sc?.level === 'h' ? 'var(--red)' : sc?.level === 'm' ? 'var(--ora)' : 'var(--blu)';
      svg += `<circle cx="${x2}" cy="${y2}" r="25" fill="${col}22" stroke="${col}" stroke-width="1.2"/>`;
      svg += `<text x="${x2}" y="${
        y2 + 4
      }" text-anchor="middle" fill="${col}" font-size="8" font-family="JetBrains Mono">${`...${c.slice(
        s.id.length - 6
      )}`}</text>`;
    });
    if (!conns.length)
      svg += `<text x="${cx}" y="${
        cy + 50
      }" text-anchor="middle" fill="rgba(255,255,255,.18)" font-size="12" font-family="Sora">${t(
        'suspectProfile.noKnownConnections'
      )}</text>`;
    return svg + '</svg>';
  }

  if (!suspect) return null;
  const s = suspect;

  return (
    <div className={`pp ${suspect ? 'open' : ''}`}>
      <div className="pp-head">
        <div className="pp-top">
          <div className={`pp-id ${s.level}`}>{s.id}</div>
          <div className="pp-close" onClick={onClose}>
            ✕
          </div>
        </div>
        {/* TODO: Return once we have it */}
        <div className="pp-row">
          <span className={`pp-pro ${s.level}`}>{s.profiles?.join(', ')}</span>
          <span className="pp-score">
            {t('suspectProfile.score')} {score}
          </span>
          <div className="score-bar-lg" style={{ flex: 1, marginLeft: 8 }}>
            <div className={`sf-lg ${s.level}`} style={{ width: `${score}%` }} />
          </div>
        </div>
      </div>
      <div className="pp-body">
        <div className="pp-section">
          <div className="pp-sh">{t('suspectProfile.identityDevice')}</div>
          <div className="pp-grid">
            <div className="pp-field">
              <div className="pp-fl">{t('suspectProfile.deviceId')}</div>
              <div className="pp-fv" style={{ fontSize: 9 }}>
                {s.deviceId}
              </div>
            </div>
            <div className="pp-field">
              <div className="pp-fl">{t('suspectProfile.origin')}</div>
              <div className={`pp-fv ${suspect.hostileCountries?.includes(s.countries[0]) ? 'text-hot' : ''}`}>
                {s.origin}
              </div>
            </div>
            <div className="pp-field">
              <div className="pp-fl">{t('suspectProfile.ageGender')}</div>
              <div className="pp-fv">
                {s.age} / {s.gender}
              </div>
            </div>
            <div className="pp-field">
              <div className="pp-fl">{t('suspectProfile.languages')}</div>
              <div className={`pp-fv ${suspect.hostileLang ? 'text-hot' : ''}`}>{s.lang}</div>
            </div>
            <div className="pp-field" style={{ gridColumn: '1/-1' }}>
              <div className="pp-fl">{t('suspectProfile.homeAddress')}</div>
              <div className="pp-fv">{s.homeAddr}</div>
            </div>
            <div className="pp-field" style={{ gridColumn: '1/-1' }}>
              <div className="pp-fl">{t('suspectProfile.device')}</div>
              <div className="pp-fv">{s.device}</div>
            </div>
          </div>
        </div>

        <div className="pp-section">
          <div className="pp-sh">{t('suspectProfile.countriesVisited')}</div>
          <div className="tag-row">
            {s.countries.map((country) => (
              <span key={country.cc2} className={`tag ${s.hostileCountries?.includes(country) ? 'bad' : 'neu'}`}>
                {`${getFlagEmoji(country.cc2)} ${country.name}`}
              </span>
            ))}
          </div>
        </div>

        <div className="pp-section">
          <div className="pp-sh">{t('suspectProfile.appsInstalled')}</div>
          {s.apps.map((a) => {
            const isHostile = s.hostileApps?.includes(a);
            return (
              <div
                key={a.bundle}
                className={`freq-item app-item${isHostile ? ' hostile-app' : ''}`}
                onClick={() => onAppClick(a.bundle)}
              >
                <div className={`freq-dot ${isHostile ? 'h' : 'n'}`} />
                <span
                  className={`tag ${isHostile ? 'bad' : 'neu'}`}
                  style={{ fontSize: 11, color: isHostile ? 'var(--ora)' : 'var(--t2)' }}
                >
                  {a.name || a.bundle}
                </span>
                <span className="app-chevron">›</span>
              </div>
            );
          })}
        </div>

        {/* TODO: Return once we have it */}
        <div className="pp-section">
          <div className="pp-sh">{t('suspectProfile.reasonsForSuspicion')}</div>
          {s.reasons.map((r, i) => (
            <div key={i} className="reason-item">
              <div className="reason-dot" />
              {r}
            </div>
          ))}
        </div>

        <div className="pp-section">
          <div className="pp-sh">{t('suspectProfile.frequentLocations')}</div>
          <div className="pp-scroll">
            {s.freqLocs.map((f, i) => (
              <div key={i} className="freq-item">
                <div className={`freq-dot ${f.h ? 'h' : 'n'}`} />
                <span style={{ fontSize: 11, color: f.h ? 'var(--ora)' : 'var(--t2)' }}>{f.n}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="pp-section">
          <div className="pp-sh">{t('suspectProfile.socialGraph')}</div>
          <div dangerouslySetInnerHTML={{ __html: socialSVG(s) }} />
        </div>

        <div className="pp-section">
          <div className="pp-sh">{t('suspectProfile.alertHistory')}</div>
          <div className="pp-scroll">
            {alerts?.map((a, i) => (
              <div key={i} className={`hist-item ${a.t}`}>
                <div className="hist-top">
                  <span className={`hist-t ${a.t}`}>
                    {a.t === 'c'
                      ? t('suspectProfile.alertCritical')
                      : a.t === 'w'
                      ? t('suspectProfile.alertWarning')
                      : t('suspectProfile.alertInfo')}
                  </span>
                  <span className="hist-time">{a.time}</span>
                </div>
                <div className="hist-body">{a.body}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="pp-section">
          <div className="pp-sh">{t('suspectProfile.analystNotes')}</div>
          <div style={{ position: 'relative' }}>
            <textarea
              className="notes-area"
              value={note}
              disabled={isNoteSaving}
              readOnly={readOnly}
              onChange={(e) => setNote(e.target.value)}
              onBlur={saveNote}
              placeholder={t('suspectProfile.notesPlaceholder')}
            />
            {isNoteSaving && <Spinner size={16} />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SuspectProfile;
